import React from 'react';
import { 
  TrendingUp, 
  DollarSign,
  RefreshCw,
  ArrowUpRight
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminRevenue } from '../../hooks/useAdminRevenue';

const AdminRevenue = () => {
  // Use the custom hook for revenue data
  const {
    revenue,
    isLoading,
    error,
    period,
    setPeriod,
    periodOptions,
    refetch
  } = useAdminRevenue('month');

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <p className="text-red-600">Failed to load revenue data. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Revenue
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Track and analyze platform revenue
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SearchSelect
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                className="w-40"
              />
              <button
                onClick={() => refetch()}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white border rounded-lg p-6">
            <div className="text-center">Loading revenue data...</div>
          </div>
        ) : (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      ${(revenue.overview?.total_revenue || 0).toFixed(2)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Platform Fees</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      ${(revenue.overview?.platform_fees || 0).toFixed(2)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-500" />
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Net Revenue</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      ${(revenue.overview?.net_revenue || 0).toFixed(2)}
                    </p>
                  </div>
                  <ArrowUpRight className="w-8 h-8 text-blue-500" />
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Profit Margin</p>
                    <p className="text-2xl font-bold text-indigo-600 mt-1">
                      {(revenue.overview?.profit_margin || 0).toFixed(2)}%
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-indigo-500" />
                </div>
              </div>
            </div>

            {/* Breakdown */}
            {revenue.breakdown && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="bg-white border rounded-lg p-3 sm:p-4">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue Breakdown</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">By Payment Type</span>
                    </div>
                    {revenue.breakdown.by_payment_type && (
                      <div className="space-y-2">
                        <div className="flex justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">Deposit</span>
                          <span className="text-sm font-medium">
                            ${(revenue.breakdown.by_payment_type.deposit || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">Full Payment</span>
                          <span className="text-sm font-medium">
                            ${(revenue.breakdown.by_payment_type.full_payment || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                    {revenue.breakdown.fee_breakdown && (
                      <div className="mt-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Fee Breakdown</span>
                        </div>
                        <div className="space-y-2 mt-2">
                          <div className="flex justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm">Platform Fees</span>
                            <span className="text-sm font-medium">
                              ${(revenue.breakdown.fee_breakdown.platform_fees || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm">Processor Fees</span>
                            <span className="text-sm font-medium">
                              ${(revenue.breakdown.fee_breakdown.processor_fees || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between p-2 bg-green-50 rounded">
                            <span className="text-sm font-semibold">Net Fees</span>
                            <span className="text-sm font-bold text-green-600">
                              ${(revenue.breakdown.fee_breakdown.net_fees || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Revenue by Source */}
                {revenue.by_source && (
                  <div className="bg-white border rounded-lg p-3 sm:p-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Top Revenue Sources</h2>
                    <div className="space-y-4">
                      {revenue.by_source.by_vendor && revenue.by_source.by_vendor.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Top Vendors</h3>
                          <div className="space-y-2">
                            {revenue.by_source.by_vendor.slice(0, 5).map((vendor, index) => (
                              <div key={index} className="flex justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-600">{vendor.business_name}</span>
                                <span className="text-sm font-medium">
                                  ${parseFloat(vendor.revenue || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {revenue.by_source.by_service && revenue.by_source.by_service.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Top Services</h3>
                          <div className="space-y-2">
                            {revenue.by_source.by_service.slice(0, 5).map((service, index) => (
                              <div key={index} className="flex justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-600">{service.service_name}</span>
                                <span className="text-sm font-medium">
                                  ${parseFloat(service.revenue || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Projections */}
            {revenue.projections && (
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue Projections</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600">Average Daily</p>
                    <p className="text-lg font-bold">
                      ${(revenue.projections.average_daily_revenue || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600">Projected Monthly</p>
                    <p className="text-lg font-bold">
                      ${(revenue.projections.projected_monthly || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600">Projected Quarterly</p>
                    <p className="text-lg font-bold">
                      ${(revenue.projections.projected_quarterly || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600">Growth Rate</p>
                    <p className={`text-lg font-bold ${(revenue.projections.growth_rate || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(revenue.projections.growth_rate || 0).toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminRevenue;

